/* eslint-disable react/prop-types */
import { Edit, Visibility } from '@mui/icons-material'
import { Box, IconButton, Typography } from '@mui/material'
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../app/hooks'
import { useGetAllTransportModesQuery } from '../../app/redux-fetch/apiCoreQuery'
import { useGetCustomersQuery } from '../../app/redux-fetch/apiQuery'
import { useGetJourneysByTenantIdQuery } from '../../app/redux-fetch/apiOrders'
import AddButton from '../../components/Buttons/AddButton'
import MRTDataGridv2 from '../../components/MRTDataGrid/MRTDataGridv2'
import { fetchLoggedInUserSelector } from '../selectors'
import { ILoggedInUser } from '../types'
import EmbeddedLegTable from './Leg/EmbeddedLegTable'
import EmbeddedLoadTable from './Load/EmbeddedLoadTable'
import EmbeddedRoutesTable from './Route/EmbeddedRouteTable'
import ViewJourneyAction from './components/ViewJourneyAction'
import useJourneyStepperContext from './hooks/useJourneyStepperContext'
import { messages } from './messages'
import { Journey, JourneyStatusTypeId, JourneyStatusTypeLabels } from './types'
import { countRoutes, countUniqueLoadIds, getAllRoutes, getAllUniqueLoads } from './utils'

const JourneyTable = () => {

    const { formatMessage } = useIntl()

    const [selectedJourneyId, setSelectedJourneyId] = useState('not selected')

    const { tenantId } = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
    const { data: customers = [], isSuccess: isCustomersLoaded } = useGetCustomersQuery(tenantId)

    const { data: journeys, isSuccess: isJourneyLoaded } = useGetJourneysByTenantIdQuery({
        tenantId,
    })

    const { data: transportModes = [] } = useGetAllTransportModesQuery()

    const getTransportModeName = (transportModeId?: number): string => {
        if (!transportModeId) return '-'
        const mode = transportModes.find(m => m.id === transportModeId)
        return mode?.name || '-'
    }

    const getJourneyTransportMode = (journey: Journey): string => {
        if (!journey.legs || journey.legs.length === 0) return '-'
        const firstLeg = journey.legs[0]
        return getTransportModeName(firstLeg.transportModeId)
    }

    const getJourneyStatus = (journey: Journey): string => {
        const statusId = journey.journeyStatusTypeId as JourneyStatusTypeId
        return JourneyStatusTypeLabels[statusId] || (journey.isApproved ? formatMessage(messages.approvedStatus) : formatMessage(messages.draftStatus))
    }

    const [isLegSelected, setIsLegSelected] = useState(false)
    const [isRouteSelected, setIsRouteSelected] = useState(false)
    const [isLoadSelected, setIsLoadSelected] = useState(false)

    const { goToStep, reset, setSelectedJourney, setMode } = useJourneyStepperContext()

    const columns = useMemo<MRTColumnDef<Journey>[]>(() => {
        return [
            {
                id: 'id',
                accessorFn: (row) => row.id,
                Cell: ({ cell }: any) => {
                    return (<Typography sx={{ fontWeight: 700 }}>{cell.getValue()}</Typography>)
                },
                header: formatMessage(messages.idName),
            },
            {
                id: 'transportMode',
                accessorFn: (row) => getJourneyTransportMode(row),
                header: formatMessage(messages.transportModeName),
            },
            {
                id: 'status',
                accessorFn: (row) => getJourneyStatus(row),
                header: formatMessage(messages.statusName),
            },
            {
                accessorFn: (row) => {
                    const legsCount = row.legs?.length || 0
                    return legsCount
                },
                enableSorting: false,
                Cell: ({ cell, table, row }) => {
                    return <ViewJourneyAction text={cell.getValue<string>()} onClick={() => {
                        table.setExpanded({ [row.id]: !isLegSelected })
                        setIsLoadSelected(false)
                        setIsLegSelected(pv => !pv)
                        setIsRouteSelected(false)
                        setSelectedJourneyId(row.id)
                    }} />
                },
                Header: ({ column, header }) => {
                    if (isLegSelected) {
                        return (<Box >
                            <Typography variant="subtitle1" component="h2" style={{ textDecoration: 'underline', fontWeight: 700 }}>
                                {column.columnDef.header}
                            </Typography>
                        </Box>)
                    }
                    else {
                        return column.columnDef.header
                    }
                },
                header: formatMessage(messages.legsName),
            },
            {
                accessorFn: (row) => {
                    const routesCount = countRoutes(row)
                    return routesCount;
                },
                enableSorting: false,
                Cell: ({ cell, table, row }) => {
                    return <ViewJourneyAction text={cell.getValue<string>()} onClick={() => {
                        setSelectedJourneyId(row.id)
                        table.setExpanded({ [row.id]: !isRouteSelected })
                        setIsLoadSelected(false)
                        setIsLegSelected(false)
                        setIsRouteSelected(pv => !pv)
                    }} />
                },
                Header: ({ column, header }) => {
                    if (isRouteSelected) {
                        return (
                            <Box >
                                <Typography variant="subtitle1" component="h2" style={{ textDecoration: 'underline', fontWeight: 700 }}>
                                    {column.columnDef.header}
                                </Typography>
                            </Box>
                        );
                    }
                    else {
                        return column.columnDef.header
                    }
                },
                header: formatMessage(messages.routesName),
            },
            {
                accessorFn: (row) => {
                    const loadsCount = countUniqueLoadIds(row)
                    return loadsCount
                },
                enableSorting: false,
                Cell: ({ cell, table, row }) => {
                    return <ViewJourneyAction text={cell.getValue<string>()} onClick={() => {
                        table.setExpanded({ [row.id]: !isLoadSelected })
                        setIsLoadSelected(pv => !pv)
                        setIsLegSelected(false)
                        setIsRouteSelected(false)
                        setSelectedJourneyId(row.id)
                    }} />
                },
                Header: ({ column, header }) => {
                    if (isLoadSelected) {
                        return (<Box >
                            <Typography variant="subtitle1" component="h2" style={{ textDecoration: 'underline', fontWeight: 700 }}>
                                {column.columnDef.header}
                            </Typography>
                        </Box>)
                    }
                    else {
                        return column.columnDef.header
                    }
                },
                header: formatMessage(messages.loadsName),
            },
        ]
    }, [isRouteSelected, isLegSelected, isLoadSelected, selectedJourneyId, journeys, transportModes])

    return (

        <>
            <MRTDataGridv2
                initialState={{
                    pagination: { pageSize: 100, pageIndex: 0 },
                    sorting: [
                    {
                    id: 'id',
                    desc: false,
                    }]}
                }
                state={{ isLoading: !(isJourneyLoaded && isCustomersLoaded) }}
                rightHeadingComponent={
                    <AddButton
                        text={formatMessage(messages.newJourney)}
                        onClick={() => {
                            reset()
                            setMode('create')
                            goToStep(1)
                        }}
                    />
                }
                columns={columns}
                data={journeys || []}
                getRowId={(row) => row.id as any}
                enableEditing
                withChild={true}
                enableSorting={true}
                enableFilters={false}
                enableColumnActions={false}
                enableExpandAll={true}
                enableRowActions
                positionActionsColumn='last'
                muiExpandButtonProps={({ table }) => ({
                    onClick: () => {
                        if (isLegSelected || isRouteSelected || isLoadSelected) {
                            setIsLegSelected(false)
                            setIsRouteSelected(false)
                            setIsLoadSelected(false)
                        }
                        table.setExpanded({})
                    },
                })}
                renderDetailPanel={({ row }) => {
                    if (isLegSelected) {
                        return (
                            <EmbeddedLegTable legs={row.original.legs} journey={row.original} />
                        )
                    }
                    else if (isRouteSelected) {
                        return (
                            <EmbeddedRoutesTable routes={getAllRoutes(row.original)} journey={row.original} />
                        )
                    }
                    else if (isLoadSelected) {
                        return (
                            <EmbeddedLoadTable loads={getAllUniqueLoads(row.original)} journey={row.original} />
                        )
                    }
                }}
                renderRowActions={({ row, table }: any) => {
                    return (
                        <Box sx={{ display: 'flex' }}>
                            <IconButton
                                color={'info'}
                                aria-label="View"
                                onClick={() => {
                                    setMode('view')
                                    goToStep(3)
                                    setSelectedJourney(row.original)
                                }}
                            >
                                <Visibility />
                            </IconButton>
                            {!row.original.isApproved && (
                                <IconButton
                                    color={'info'}
                                    aria-label="Edit"
                                    onClick={() => {
                                        setMode('edit')
                                        setSelectedJourney(row.original)
                                        goToStep(3)
                                    }}
                                >
                                    <Edit />
                                </IconButton>
                            )}
                        </Box>
                    )
                }}
            />
        </>

    )
}

export default JourneyTable
